import { type DragEvent, useCallback, useState } from "react";
import { subirEvidencia } from "@/components/evidencias/subirEvidencia";
import type { EvidenciaOut } from "@/lib/api";
import { useToast } from "@/ui/Toast";

type Args = {
  casoId: number;
  fotos: EvidenciaOut[];
  onChange: (fotos: EvidenciaOut[]) => void;
};

/**
 * Subida de las fotos de constancia de la bitácora.
 *
 * Vive en un hook —y no dentro del componente del botón— porque hay DOS
 * disparadores para lo mismo: el botón «Fotos» de la fila de controles y el
 * soltar un archivo en cualquier parte del compositor. Los dos tienen que
 * compartir el estado de "subiendo" y la lista.
 *
 * La foto se sube al caso EN CUANTO se suelta (queda en Evidencias con la
 * etiqueta `bitacora`) y la nota solo guarda a cuáles apunta: si el usuario
 * abandona la nota, la evidencia no se pierde.
 */
export function useFotosBitacora({ casoId, fotos, onChange }: Args) {
  const toast = useToast();
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState(false);

  const agregar = useCallback(
    async (lista: FileList | File[]) => {
      const archivos = Array.from(lista);
      const imagenes = archivos.filter((f) => f.type.startsWith("image/"));
      if (imagenes.length < archivos.length) {
        toast.error("Solo imágenes", "En la bitácora únicamente se pueden adjuntar fotos.");
      }
      if (imagenes.length === 0) return;

      const nuevas: EvidenciaOut[] = [];
      for (const file of imagenes) {
        setSubiendo(file.name);
        try {
          const ev = await subirEvidencia({
            casoId,
            file,
            comprimir: false,
            tags: ["bitacora"],
          });
          nuevas.push(ev);
        } catch (err) {
          toast.error(`No se pudo subir ${file.name}`, (err as Error).message);
        }
      }
      setSubiendo(null);
      if (nuevas.length) {
        // El backend deduplica por sha256: soltar la misma captura dos veces
        // devuelve el MISMO id. Se filtra aquí para no pintarla repetida.
        const ids = new Set(fotos.map((f) => f.id));
        onChange([...fotos, ...nuevas.filter((n) => !ids.has(n.id))]);
      }
    },
    // `toast` devuelve un objeto nuevo en cada render: incluirlo recrearía el
    // callback siempre (el bucle infinito que ya mordió en ProveedorPendiente).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [casoId, fotos, onChange],
  );

  // Handlers para que el compositor COMPLETO acepte que le suelten fotos: un
  // botón de ~100px es mal blanco de arrastre. Se spread-ean sobre el `card`.
  const dropProps = {
    onDragEnter: (e: DragEvent<HTMLElement>) => {
      if (e.dataTransfer.types.includes("Files")) setArrastrando(true);
    },
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (e.dataTransfer.types.includes("Files")) e.preventDefault();
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      // Solo apagar al salir del compositor, no al cruzar entre sus hijos.
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        setArrastrando(false);
      }
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setArrastrando(false);
      void agregar(e.dataTransfer.files);
    },
  };

  return { agregar, subiendo, arrastrando, dropProps };
}
